import { useMemo } from 'react';
import { DegreeType, useGetCoursesQuery, useGetEvaluationQuery } from '@teachur/http-client';

import { getDegreeTotalCredits } from '../utils/degreeCreditRequirements';

export interface UnifiedCredits {
  earned: {
    fromCourses: number;
    total: number;
  };
  transfer: {
    total: number;
    official: number;
    unofficial: number;
  };
  total: number;
  progressPercentage: number;
}

export const useUnifiedCredits = (degreeType?: DegreeType) => {
  const { data: userCoursesData } = useGetCoursesQuery(
    { page: 1, pageSize: 1000 },
    { staleTime: Infinity }
  );
  const { data: evaluation } = useGetEvaluationQuery();

  const unifiedCredits = useMemo((): UnifiedCredits => {
    const userCourses = userCoursesData?.data || [];

    const completedCourses = userCourses.filter(
      (course) =>
        (course.status === 'completed' || course.coursePassed) &&
        course.forCredit &&
        (course.credit || 0) > 0
    );

    const earnedFromCourses = completedCourses.reduce(
      (sum, course) => sum + (course.credit || 0),
      0
    );

    const official = evaluation?.officialTotal || 0;
    const unofficial = evaluation?.unofficialTotal || 0;
    const transferCredits = {
      total: official + unofficial,
      official,
      unofficial,
    };

    const totalCredits = earnedFromCourses + transferCredits.total;

    const mandatoryCredits = getDegreeTotalCredits(degreeType);
    const progressPercentage =
      mandatoryCredits > 0 ? Math.round((totalCredits / mandatoryCredits) * 100) : 0;

    return {
      earned: {
        fromCourses: earnedFromCourses,
        total: earnedFromCourses,
      },
      transfer: transferCredits,
      total: totalCredits,
      progressPercentage,
    };
  }, [evaluation, userCoursesData, degreeType]);

  return unifiedCredits;
};
